/* eslint-disable react/jsx-props-no-spreading */
import React, { CSSProperties } from 'react';
import { FiChevronDown } from 'react-icons/fi';

import BoxMenu from '@/components/BoxMenu';
import BoxMenuOption from '@/components/BoxMenu/BoxMenuOption';
import colors from '@/design/colors';

import {
  Field,
  HelperText,
  InputContent,
  SelectButton,
  SelectDropdown,
  SelectIcon,
  SelectLabel,
  SelectWrapper,
  Title,
} from './styles';

interface FieldLayoutProps extends React.PropsWithChildren {
  title?: string;
  error?: string;
  style?: CSSProperties;
  helperText?: string;
}

export interface InputFieldSelectOption {
  label: string;
  value: string;
  disabled?: boolean;
}

export interface InputFieldSelectProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'onChange'> {
  title?: string;
  inputTitle?: string; // Standart HTML title attribute
  error?: string;
  helperText?: string;
  readOnly?: boolean;
  options: InputFieldSelectOption[];
  value?: string;
  defaultValue?: string;
  onChange?: (value: string, option: InputFieldSelectOption) => void;
}

const FieldLayout: React.FC<FieldLayoutProps> = ({
  error,
  title,
  style,
  children,
  helperText,
}) => (
  <Field hasError={!!error} style={style}>
    {title && <Title>{title}</Title>}
    <InputContent>{children}</InputContent>
    {helperText && <HelperText>{helperText}</HelperText>}
    {error && <HelperText>{error}</HelperText>}
  </Field>
);

const InputFieldSelect = React.forwardRef<
  HTMLButtonElement,
  InputFieldSelectProps
>(
  (
    {
      title,
      error,
      helperText,
      inputTitle,
      options,
      value,
      defaultValue,
      onChange,
      disabled,
      readOnly,
      style,
      ...buttonProps
    },
    ref,
  ) => {
    const rootRef = React.useRef<HTMLDivElement>(null);
    const buttonInnerRef = React.useRef<HTMLButtonElement>(null);
    const [opened, setOpened] = React.useState(false);

    const firstEnabledOption = React.useMemo(
      () => options.find(option => !option.disabled),
      [options],
    );

    const isControlled = value !== undefined;
    const [internalValue, setInternalValue] = React.useState<
      string | undefined
    >(defaultValue ?? firstEnabledOption?.value);

    React.useEffect(() => {
      if (!isControlled && !internalValue && firstEnabledOption) {
        setInternalValue(firstEnabledOption.value);
      }
    }, [firstEnabledOption, internalValue, isControlled]);

    React.useEffect(() => {
      if (disabled || readOnly) {
        setOpened(false);
      }
    }, [disabled, readOnly]);

    const selectedValue = isControlled ? value : internalValue;
    const selectedOption =
      options.find(option => option.value === selectedValue) ??
      firstEnabledOption;

    const handleToggleMenu = React.useCallback(() => {
      if (disabled || readOnly || options.length === 0) {
        return;
      }

      setOpened(prev => !prev);
    }, [disabled, readOnly, options.length]);

    const handleOptionSelect = React.useCallback(
      (
        event: React.MouseEvent<HTMLAnchorElement>,
        option: InputFieldSelectOption,
      ) => {
        event.preventDefault();

        if (option.disabled) {
          return;
        }

        if (!isControlled) {
          setInternalValue(option.value);
        }

        onChange?.(option.value, option);
        setOpened(false);
      },
      [isControlled, onChange],
    );

    const handleClickOutside = React.useCallback((event: MouseEvent) => {
      if (!rootRef.current) {
        return;
      }

      if (!rootRef.current.contains(event.target as Node)) {
        setOpened(false);
      }
    }, []);

    React.useEffect(() => {
      document.addEventListener('mousedown', handleClickOutside, false);

      return () => {
        document.removeEventListener('mousedown', handleClickOutside, false);
      };
    }, [handleClickOutside]);

    React.useImperativeHandle(
      ref,
      () => buttonInnerRef.current as HTMLButtonElement,
      [],
    );

    return (
      <FieldLayout
        error={error}
        title={title}
        helperText={helperText}
        style={style}
      >
        <SelectWrapper ref={rootRef}>
          <SelectButton
            type="button"
            ref={buttonInnerRef}
            title={inputTitle}
            opened={opened && !readOnly}
            disabled={disabled || options.length === 0}
            data-read-only={readOnly || undefined}
            aria-readonly={readOnly}
            onClick={handleToggleMenu}
            {...buttonProps}
          >
            <SelectLabel>{selectedOption?.label ?? ''}</SelectLabel>
            <SelectIcon>
              <FiChevronDown size={20} color={colors.gray200} />
            </SelectIcon>
          </SelectButton>

          {opened && (
            <SelectDropdown>
              <BoxMenu>
                {options.map(option => (
                  <BoxMenuOption
                    key={option.value}
                    href="#"
                    disabled={option.disabled}
                    onClick={event => handleOptionSelect(event, option)}
                  >
                    {option.label}
                  </BoxMenuOption>
                ))}
              </BoxMenu>
            </SelectDropdown>
          )}
        </SelectWrapper>
      </FieldLayout>
    );
  },
);

export default InputFieldSelect;
